<template>
    <div class="relative print:hidden z-40 hidden desktop:block">
        <!-- visible nav links / these will need to appear above the main slider -->
        <div class="px-16 py-5 bg-white">
            <div class="flex justify-between items-center">
                <div class="w-64">
                    <AppLogo :logo-link="logoLink" :target="target" />
                </div>
                <ul data-qa="app_nav_menu_top_level" class="list-inline z-10 items-center">
                    <li v-if="currentSiteSection === 'business' && phoneNumber"
                        class="js-navItem">
                        <a :href="`tel:${phoneNumber}`"
                           class="flex items-center text-navy-dark font-bold text-lg widescreen:text-2lg z-50 leading-tight"
                        >
                            <IconPhone class="h-4 mr-2" />
                            {{ phoneNumber }}
                        </a>
                    </li>
                    <li v-for="(mainMenu, index) in menu"
                        v-if="canShow(mainMenu.audience)"
                        :key="index"
                        class="js-navItem"
                    >
                        <a  v-if="mainMenu.items && mainMenu.items.length"
                            @click.prevent="showSubMenuOf(mainMenu)"
                            :href="mainMenu.url"
                            :data-qa="`nav_title_${formatDataQa(mainMenu.text)}`"
                            class="flex items-center font-bold relative text-lg widescreen:text-2lg z-50"
                            :class="subMenuSliderIsVisible && mainMenuSelected === mainMenu? 'text-salmon': 'text-navy-dark' "
                        >
                            {{ mainMenu.text }}
                        </a>
                        <a v-else
                           :href="mainMenu.url"
                           :target="target"
                           :data-qa="`nav_title_${formatDataQa(mainMenu.text)}`"
                           class="text-navy-dark font-bold text-lg widescreen:text-2lg"
                        >
                            {{ mainMenu.text }}
                        </a>

                        <!-- submenu -->
                        <div v-show="mainMenuSelected === mainMenu"
                             class="fixed -z-1 h-full w-full right-0 top-0"
                            @click="closeIfOutsideMenus"
                        >
                            <div ref="menus"
                                 class="absolute right-0 bg-white flex flex-col h-full pt-48 shadow w-1/2 widescreen:w-202 "
                            >
                                <div data-qa="app_nav_menu_sub_level" class="flex flex-col overflow-x-hidden overflow-y-auto px-16 w-full">
                                    <div v-for="(subMenu, i) in mainMenu.items"
                                         :key="i"
                                         v-if="canShow(subMenu.audience)"
                                         class="mb-10 flex flex-row justify-start items-center">
                                        <button v-if="subMenu.items && subMenu.items.length && !subMenu.url"
                                            :class="{ 'text-salmon': subMenuSelected === subMenu }"
                                            class="block relative text-left w-full"
                                            @click.prevent="showExtraMenuOf(subMenu)"
                                        >
                                            <div class="absolute -left-12 top-1/2 transform translate-x-4 -translate-y-1/2 scale-150">
                                                <IconChevronLeft />
                                            </div>
                                            <div :data-qa="`sub_nav_title_${formatDataQa(subMenu.text)}`"
                                                class="font-bold text-lg">
                                                {{ subMenu.text }}
                                            </div>
                                            <p>{{ subMenu.description }}</p>
                                        </button>
                                        <a v-else-if="subMenu.items && subMenu.items.length"
                                            :href="subMenu.url"
                                            :class="{ 'text-salmon': subMenuSelected === subMenu }"
                                            class="block relative w-full"
                                            @click.prevent="showExtraMenuOf(subMenu)"
                                        >
                                            <div class="absolute -left-12 top-1/2 transform translate-x-4 -translate-y-1/2 scale-150">
                                                <IconChevronLeft />
                                            </div>
                                            <div class="font-bold text-lg"
                                                :data-qa="`sub_nav_title_${formatDataQa(subMenu.text)}`">
                                                {{ subMenu.text }}
                                            </div>
                                            <p>{{ subMenu.description }}</p>
                                        </a>
                                        <a v-else
                                           :href="subMenu.url"
                                           class="block">
                                            <div class="font-bold text-lg"
                                                :data-qa="`sub_nav_title_${formatDataQa(subMenu.text)}`">
                                                {{ subMenu.text }}
                                            </div>
                                            <p>{{ subMenu.description }}</p>
                                        </a>

                                        <!-- subsubMenu -->
                                        <transition name="slide-left">
                                            <div v-show="subMenuSelected === subMenu"
                                                class="-z-1 bg-gray-200 fixed flex flex-col h-full right-1/2 widescreen:right-162 overflow-x-hidden overflow-y-auto pt-48 px-10 widescreen:px-16 shadow top-0 w-1/4"
                                            >
                                                <div class="w-full flex flex-col">
                                                    <div v-for="(extraItem, i) in subMenu.items"
                                                         v-if="canShow(extraItem.audience)"
                                                         :key="i"
                                                         class="mb-8">
                                                        <a :href="extraItem.url"
                                                           class="block">
                                                            <div class="font-bold"
                                                                :data-qa="`sub_sub_nav_title_${formatDataQa(extraItem.text)}`">
                                                                {{ extraItem.text }}
                                                            </div>
                                                            <p class="text-sm">{{ extraItem.description }}</p>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- tint/overlay -->
        <transition name="fade">
            <div v-show="subMenuSliderIsVisible"
                class="fixed w-full h-full inset-0 bg-black opacity-75"
            />
        </transition>
    </div>
</template>

<script>
    import IconChevronDown from "@/atoms/icons/IconChevronDown";
    import IconChevronRight from "@/atoms/icons/IconChevronRight";
    import IconPhone from "@/atoms/icons/IconPhone";
    import IconSearch from "@/atoms/icons/IconSearch";
    import AppLogo from '@/atoms/AppLogo/appLogo';
	import IconChevronLeft from "../../atoms/icons/IconChevronLeft.vue";

    const CSS_TRANSITION_DURATION = 300

    export default {
        name: 'AppNavMenu',
        components: {
			IconChevronLeft,
            IconChevronDown,
            IconChevronRight,
            IconPhone,
            IconSearch,
            AppLogo,
        },
        props: {
            // Note: level 1 objects are the "main" menus, they are always visible on the page and usually have more levels
            //       level 2 objects are the "sub" menus, they appear in the main slider
            //       level 2 objects may have a third level called "extra" menus, which appears in the sub slider
            menu: {
                type: Array,
                required: true,
            },
            currentSiteSection: {
                type: String,
                required: true,
            },
            logoLink: {
                type: String,
                required: true,
            },
            ctas: {
                type: Array,
                default: () => [],
            },
            phoneNumber: {
                type: String,
                required: false,
            },
            includeSearch: {
                type: Boolean,
                default: () => true,
            },
            target: {
                type: String,
                default: null,
            },
            canShow: {
                type: Function,
                required: true,
            },
        },
        data: () => ({
            // todo: on smaller breakpoints when the nav gets hidden by CSS, reset all these values
            mainMenuSelected: null,
            subMenuSelected: null,
            subMenuSliderIsVisible: false,
            extraMenuSliderIsVisible: false,
        }),
        mounted() {
            if(document.getElementById('appNavSkeleton')){
                document.getElementById('appNavSkeleton').remove()
            }
        },
        methods: {
            formatDataQa(text) {
                return text
                    .toLowerCase()                     // Convert to lowercase
                    .replace(/[^a-z0-9\s]/g, '')       // Remove everything except lowercase letters, numbers, and spaces
                    .replace(/\s+/g, '_')              // Replace spaces with underscores
                    .replace(/_+$/, '')                // Remove trailing underscores
                    .trim();                           // Trim any whitespace
            },
            openNav() {
                this.subMenuSliderIsVisible = true
                this.$emit('overlay-opened')
                addEventListener('focusin', this.checkFocus)
                addEventListener('keyup', this.closeIfEsc)
            },

            closeNav() {
                this.hideAllSliders() // to also hide the sub menu if it's open
                removeEventListener('focusin', this.checkFocus)
                addEventListener('keyup', this.closeIfEsc)
            },

            checkFocus(evt) {
                const navItems = [...this.$el.querySelectorAll('.js-navItem')]
                if (!navItems.some(e => e.contains(evt.target))) {
                    this.hideAllSliders()
                    removeEventListener('focusin', this.checkFocus)
                }
            },

            closeIfOutsideMenus(evt) {
                if (this.$refs.menus.every(e => !e.contains(evt.target))) {
                    this.closeNav()
                }
            },

            closeIfEsc(evt) {
                if (evt.key === 'Escape') {
                    this.closeNav();
                }
            },

            showSubMenuOf (mainMenu) {
                // clicking on the currently-selected main menu will deselect it
                if (this.subMenuSliderIsVisible) {
                    if (this.mainMenuSelected === mainMenu) {
                        this.closeNav()
                    } else {
                        // sub menu slider is already visible but user just clicked on a different main menu
                        this.mainMenuSelected = mainMenu
                        this.hideExtraMenuSlider()
                    }
                } else {
                    this.mainMenuSelected = mainMenu
                    this.openNav()
                }
            },
            hideSubMenuSlider () {
                this.subMenuSliderIsVisible = false
                this.$emit('overlay-closed')

                // Clear selected after slider has finished close animation
                setTimeout(() => {
                    this.mainMenuSelected = null
                }, CSS_TRANSITION_DURATION)
            },
            showExtraMenuOf (subMenu) {
                // clicking on the currently-selected sub menu will deselect it
                if (this.extraMenuSliderIsVisible) {
                    if (this.subMenuSelected === subMenu) {
                        this.hideExtraMenuSlider()
                    } else {
                        this.subMenuSelected = subMenu
                    }
                } else {
                    this.extraMenuSliderIsVisible = true
                    this.subMenuSelected = subMenu
                }
            },
            hideExtraMenuSlider () {
                this.extraMenuSliderIsVisible = false

                // Clear selected after slider has finished close animation
                setTimeout(() => {
                    this.subMenuSelected = null
                }, CSS_TRANSITION_DURATION)
            },
            hideAllSliders () {
                // Stagger if needed (extra slider should close first, then sub)
                setTimeout(() => {
                    this.hideSubMenuSlider()
                }, this.extraMenuSliderIsVisible ? CSS_TRANSITION_DURATION * 1.25 : 0)

                this.hideExtraMenuSlider()
            },
        },
        computed: {
            currentPage() {
                let pageTitle = window.location.pathname.replace(/-/g, ' ').replace(/\\|\//g,' ').substring(1);
                if(pageTitle == ""){
                    pageTitle = "home";
                }
                return pageTitle;
            }
        },
    }
</script>

<style lang="scss" scoped>
    .sequential-fade-enter, .sequential-fade-leave-to {
        opacity: 0;
    }
    .sequential-fade-enter-active {
        transition: all 150ms 300ms;
    }
    .sequential-fade-leave-active {
        transition: all 150ms;
    }
</style>
